/**
 * PayrollWizardReadOnlyDialog
 *
 * A read-only mirror of Payroll Wizard Step 4. Used by:
 *   • Talent → Pending Approval Log → ⋮ → View
 *   • PMS Admin → Approve Payroll → ⋮ → View
 *
 * The dialog shows the same per-employee table the user saw in the wizard
 * before clicking "Submit for Approval" — gross earnings, paid-in-advance
 * group, real deductions, NPP / Rider-Pay / EMI-adjustment markers, arrears,
 * and net. Everything is non-interactive.
 *
 * Props:
 *   open          → controls Dialog open state
 *   onOpenChange  → standard shadcn Dialog setter
 *   batch         → the payroll_batches document (id, month, year, platform,
 *                   payroll_type, totals, status, created_by_name, etc.)
 *   payrolls      → array of monthly_payrolls documents tagged to the batch
 */
import React, { useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from './ui/table';
import { AlertTriangle, Banknote, FileText, Eye, ClockAlert } from 'lucide-react';

const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];

function fmt(n) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function PayrollWizardReadOnlyDialog({ open, onOpenChange, batch, payrolls, onOpenCalcView }) {
  const rows = payrolls || [];

  // Totals row computed from the rendered payrolls so it always matches
  // what's on screen (in case any field was stripped server-side).
  const totals = useMemo(() => {
    const acc = {
      gross: 0, paid_in_advance: 0, fines: 0, salik: 0, maintenance: 0,
      loan_emi: 0, slx: 0, clawback: 0, salary_advance: 0,
      prev_arrears: 0, past_dues: 0, misc: 0, net: 0,
    };
    for (const p of rows) {
      acc.gross += Number(p.gross_salary || p.gross_earnings || 0);
      acc.paid_in_advance +=
        Number(p.paid_in_wallet || 0) + Number(p.paid_as_tips || 0) + Number(p.paid_as_cod || 0);
      acc.fines += Number(p.fines || 0);
      acc.salik += Number(p.salik || 0);
      acc.maintenance += Number(p.maintenance || 0);
      acc.loan_emi += Number(p.loan_emi || 0);
      acc.slx += Number(p.slx_deduction || 0);
      acc.clawback += Number(p.clawback_deduction || 0);
      acc.salary_advance += Number(p.salary_advance || 0);
      acc.prev_arrears += Number(p.prev_arrears || 0);
      acc.past_dues += Number(p.past_dues || 0);
      acc.misc += Number(p.misc_deduction || 0);
      acc.net += Number(p.net_salary || 0);
    }
    return acc;
  }, [rows]);

  if (!batch) return null;

  const monthLabel = `${MONTHS[batch.month] || batch.month} ${batch.year}`;
  const submittedBy = batch.created_by_name || batch.created_by || '—';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] xl:max-w-7xl h-[88vh] flex flex-col" data-testid="wizard-readonly-dialog">
        <DialogHeader className="shrink-0 border-b pb-3">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <FileText className="w-5 h-5 text-emerald-600" />
            Payroll Wizard — Read-only Preview
          </DialogTitle>
          <DialogDescription>
            Same view you saw before clicking <strong>Submit for Approval</strong>. No edits possible from this dialog.
          </DialogDescription>
        </DialogHeader>

        {/* Header chips — mirrors wizard summary strip */}
        <div className="shrink-0 flex flex-wrap items-center gap-2 py-2 px-1 border-b">
          <Badge variant="outline" className={batch.payroll_type === 'rider' ? 'bg-green-50 text-green-700' : 'bg-purple-50 text-purple-700'}>
            {batch.payroll_type === 'rider' ? 'Rider' : 'Staff'}
          </Badge>
          <Badge variant="outline" className="bg-slate-50">{batch.platform || '—'}</Badge>
          <Badge variant="outline" className="bg-blue-50 text-blue-700">{monthLabel}</Badge>
          <Badge variant="outline" className={batch.status === 'pending' ? 'bg-amber-50 text-amber-700' : batch.status === 'approved' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}>
            {batch.status?.toUpperCase() || 'PENDING'}
          </Badge>
          <div className="ml-auto flex items-center gap-3 text-xs text-slate-600">
            <span><strong className="text-slate-800">Employees:</strong> {rows.length || batch.total_employees}</span>
            <span><strong className="text-emerald-700">Gross:</strong> AED {fmt(batch.total_gross || totals.gross)}</span>
            <span><strong className="text-red-700">Deductions:</strong> AED {fmt(batch.total_deductions)}</span>
            <span><strong className="text-blue-700">Net:</strong> AED {fmt(batch.total_net || totals.net)}</span>
            <span className="border-l pl-3"><strong>Submitted by:</strong> {submittedBy}</span>
          </div>
        </div>

        {/* Main per-employee table — native overflow on BOTH axes so the
            widest columns (Net, View) stay reachable on smaller laptops. */}
        <div className="flex-1 min-h-0 overflow-auto border rounded">
          <Table className="min-w-[1400px]">
            <TableHeader className="sticky top-0 bg-white z-10 border-b">
              <TableRow className="text-[11px] uppercase tracking-wide text-slate-600">
                <TableHead className="w-[200px] min-w-[200px] max-w-[200px]">Employee</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead className="text-right text-blue-700">Paid in Adv.</TableHead>
                  <TableHead className="text-right">Fines</TableHead>
                  <TableHead className="text-right">Salik</TableHead>
                  <TableHead className="text-right">Maint.</TableHead>
                  <TableHead className="text-right">Loan EMI</TableHead>
                  <TableHead className="text-right">SLX</TableHead>
                  <TableHead className="text-right">Clawback</TableHead>
                  <TableHead className="text-right">Salary Adv.</TableHead>
                  <TableHead className="text-right">Prev. Arrears</TableHead>
                  <TableHead className="text-right">Past Dues</TableHead>
                  <TableHead className="text-right">Misc.</TableHead>
                  <TableHead className="text-right text-blue-700">Net</TableHead>
                  <TableHead className="text-center w-10">View</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow><TableCell colSpan={15} className="text-center text-slate-500 py-8 text-sm">
                    No payroll records found for this batch.
                  </TableCell></TableRow>
                ) : rows.map((p, i) => {
                  const paidInAdv = Number(p.paid_in_wallet || 0) + Number(p.paid_as_tips || 0) + Number(p.paid_as_cod || 0);
                  const isNpp = !!p.is_npp;
                  const hasRiderPay = Number(p.rider_pay_fines_total || 0) > 0;
                  const emiAdjusted = !!p.emi_was_adjusted;
                  const heldOrArr = !!(p.salary_held || p.hold_salary || p.negative_salary_converted);
                  return (
                    <TableRow key={p.id || i} className="text-xs hover:bg-slate-50">
                      <TableCell className="w-[200px] min-w-[200px] max-w-[200px] align-top">
                        <div className="flex flex-col gap-0.5">
                          <p className="font-medium text-slate-800 text-[11px] leading-tight break-words whitespace-normal">{p.employee_name || '—'}</p>
                          <p className="text-[10px] text-slate-500 font-mono">{p.employee_unique_id || ''}</p>
                          <div className="flex flex-wrap gap-0.5 mt-0.5">
                            {isNpp && (
                              <Badge className="bg-slate-200 text-slate-700 text-[9px] px-1 py-0" title={`Non-Primary Platform${p.npp_primary_platform ? ` (Primary: ${p.npp_primary_platform})` : ''}`}>NPP</Badge>
                            )}
                            {hasRiderPay && (
                              <Badge className="bg-blue-100 text-blue-700 text-[9px] px-1 py-0" title={`Rider-pay fines: AED ${fmt(p.rider_pay_fines_total)}`}>RP</Badge>
                            )}
                            {emiAdjusted && (
                              <Badge className="bg-amber-100 text-amber-700 text-[9px] px-1 py-0" title="EMI adjusted (paused/partial/extended)">
                                <ClockAlert className="w-2.5 h-2.5" />
                              </Badge>
                            )}
                            {heldOrArr && (
                              <Badge className="bg-red-100 text-red-700 text-[9px] px-1 py-0" title={p.negative_salary_converted ? 'Negative net → arrears' : 'Held'}>
                                <AlertTriangle className="w-2.5 h-2.5" />
                              </Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-emerald-700">{fmt(p.gross_salary || p.gross_earnings)}</TableCell>
                      <TableCell className="text-right font-mono text-blue-700">{paidInAdv ? fmt(paidInAdv) : '—'}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">{Number(p.fines || 0) ? fmt(p.fines) : '—'}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">{Number(p.salik || 0) ? fmt(p.salik) : '—'}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">{Number(p.maintenance || 0) ? fmt(p.maintenance) : '—'}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">{Number(p.loan_emi || 0) ? fmt(p.loan_emi) : '—'}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">{Number(p.slx_deduction || 0) ? fmt(p.slx_deduction) : '—'}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">{Number(p.clawback_deduction || 0) ? fmt(p.clawback_deduction) : '—'}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">{Number(p.salary_advance || 0) ? fmt(p.salary_advance) : '—'}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">{Number(p.prev_arrears || 0) ? fmt(p.prev_arrears) : '—'}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-700">{Number(p.past_dues || 0) ? fmt(p.past_dues) : '—'}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">{Number(p.misc_deduction || 0) ? fmt(p.misc_deduction) : '—'}</TableCell>
                      <TableCell className="text-right font-mono font-semibold text-blue-700">{fmt(p.net_salary)}</TableCell>
                      <TableCell className="text-center">
                        {onOpenCalcView && (
                          <button
                            type="button"
                            className="text-blue-700 hover:bg-blue-50 rounded p-1"
                            onClick={() => onOpenCalcView(p)}
                            title="View Salary Calculation (read-only)"
                            data-testid={`readonly-calc-${p.id || i}`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              {/* Totals row */}
              {rows.length > 0 && (
                <tfoot>
                  <TableRow className="bg-slate-50 border-t-2 border-slate-300 text-xs font-semibold">
                    <TableCell className="w-[200px] min-w-[200px] max-w-[200px] text-slate-700">Totals — {rows.length} {batch.payroll_type === 'rider' ? 'rider' : 'employee'}{rows.length === 1 ? '' : 's'}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-700">{fmt(totals.gross)}</TableCell>
                    <TableCell className="text-right font-mono text-blue-700">{fmt(totals.paid_in_advance)}</TableCell>
                    <TableCell className="text-right font-mono text-red-700">{fmt(totals.fines)}</TableCell>
                    <TableCell className="text-right font-mono text-red-700">{fmt(totals.salik)}</TableCell>
                    <TableCell className="text-right font-mono text-red-700">{fmt(totals.maintenance)}</TableCell>
                    <TableCell className="text-right font-mono text-red-700">{fmt(totals.loan_emi)}</TableCell>
                    <TableCell className="text-right font-mono text-red-700">{fmt(totals.slx)}</TableCell>
                    <TableCell className="text-right font-mono text-red-700">{fmt(totals.clawback)}</TableCell>
                    <TableCell className="text-right font-mono text-red-700">{fmt(totals.salary_advance)}</TableCell>
                    <TableCell className="text-right font-mono text-red-700">{fmt(totals.prev_arrears)}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-700">{fmt(totals.past_dues)}</TableCell>
                    <TableCell className="text-right font-mono text-red-700">{fmt(totals.misc)}</TableCell>
                    <TableCell className="text-right font-mono text-blue-700">{fmt(totals.net)}</TableCell>
                    <TableCell />
                  </TableRow>
                </tfoot>
              )}
            </Table>
        </div>

        {/* Legend strip */}
        <div className="shrink-0 border-t pt-2 px-1 text-[10px] text-slate-500 flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1"><Badge className="bg-slate-200 text-slate-700 text-[9px] px-1 py-0">NPP</Badge> Non-Primary Platform</span>
          <span className="flex items-center gap-1"><Badge className="bg-blue-100 text-blue-700 text-[9px] px-1 py-0">RP</Badge> Rider-Pay Fines</span>
          <span className="flex items-center gap-1"><Badge className="bg-amber-100 text-amber-700 text-[9px] px-1 py-0"><ClockAlert className="w-2.5 h-2.5" /></Badge> EMI Adjusted</span>
          <span className="flex items-center gap-1"><Badge className="bg-red-100 text-red-700 text-[9px] px-1 py-0"><AlertTriangle className="w-2.5 h-2.5" /></Badge> Held / Arrears</span>
          <span className="ml-auto flex items-center gap-1"><Banknote className="w-3 h-3 text-emerald-600" /> Amounts in AED</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
